import React, { useState, useEffect, useContext } from "react";
import { getCompanySettings, updateCompanySettings, } from '../services/api_settings';
import { AuthContext } from '../contexts/auth';
import Swal from "sweetalert2";
import { PencilSquareIcon, TrashIcon, BriefcaseIcon, ClockIcon } from "@heroicons/react/24/outline";
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';


const OrarioLavoroSetting = () => {
  const { token } = useContext(AuthContext);
  const [originalSettings, setOriginalSettings] = useState(null);
  const [isLoadingSetting, setIsLoadingSetting] = useState(false);
  const [oraInizio, setOraInizio] = useState('');
  const [oraFine, setOraFine] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isNightShiftActive, setIsNightShiftActive] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  const fetchSettings = async () => {
    try {
      setIsLoadingSetting(true);
  
      const settings = await getCompanySettings(token, 'attendance');
  
      setOriginalSettings(settings);
      setIsNightShiftActive(settings?.night_shift_active || false);
      const formatMinutesToTime = (minutes) => {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
      };
  
      setOraInizio(formatMinutesToTime(settings?.night_shift_start));
      setOraFine(formatMinutesToTime(settings?.night_shift_end));
    } catch (err) {
      setError("Errore durante il caricamento delle impostazioni.");
      console.error(err);
    } finally {
      setIsLoadingSetting(false);
    }
  };
  
  useEffect(() => {
    fetchSettings();
  }, [token]);

  const handleCheckboxChange = async () => {
    const newValue = !isNightShiftActive;

    try {
      Swal.fire({
        title: 'Salvataggio...',
        allowOutsideClick: false,
        didOpen: () => {
            Swal.showLoading();
        }
      });
      
      await updateCompanySettings(token, { night_shift_active: newValue });
      setIsNightShiftActive(newValue);
  
      if (newValue) {
        await fetchSettings();
      } else {
        setOriginalSettings((prev) => ({
          ...prev,
          night_shift_active: newValue,
        }));
      }

      Swal.fire({
        icon: "success",
        title: "Salvato!",
        showConfirmButton: false,
        timer: 1000,
      });
    } catch (error) {
      Swal.close();
      console.error("Errore durante il salvataggio:", error);

      let errorMessage = "Non è stato possibile aggiornare l'impostazione. Per favore riprova.";
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        const errorMessages = [];
  
        Object.keys(errorData).forEach((key) => {
          const messageArray = errorData[key];
          if (Array.isArray(messageArray)) {
            errorMessages.push(...messageArray);
          } else if (typeof messageArray === "string") {
            errorMessages.push(messageArray);
          }
        });
  
        if (errorMessages.length > 0) {
          errorMessage = errorMessages.join(" ");
        }
      }
      
      Swal.fire({
        icon: "error",
        title: "Errore",
        text: errorMessage,
        confirmButtonText: "OK",
      });
    }
  };

  const handleOraChange = (field) => (e) => {
    let inputValue = e.target.value.replace(/\D/g, '');
    inputValue = inputValue.slice(0, 4);

    let formattedTime = inputValue.replace(/(\d{2})(\d{0,2})/, "$1:$2");
  
    if (inputValue.length <= 2 && e.target.value.length < formattedTime.length) {
      formattedTime = formattedTime.replace(":", "");
    }
  
    if (field === 'start') {
      setOraInizio(formattedTime);
    } else {
      setOraFine(formattedTime);
    }
  };
  
  const handleWorkTimeBlur = (field) => {
    let formattedTime = field === 'start' ? oraInizio : oraFine;
  
    if (!formattedTime) {
      formattedTime = "";
    }
  
    formattedTime = formattedTime.toString().replace(/\D/g, '');
  
    while (formattedTime.length < 4) {
      formattedTime += '0';
    }
  
    formattedTime = formattedTime.slice(0, 4);
    const hours = formattedTime.slice(0, 2);
    const minutes = formattedTime.slice(2);
    formattedTime = `${hours}:${minutes}`;
  
    const isValidTime = /^(?:[01]?[0-9]|2[0-3]):[0-5][0-9]$|^24:00$/.test(formattedTime);
  
    if (!isValidTime) {
      if (field === 'start') {
        setOraInizio('');
      } else {
        setOraFine('');
      }
      return;
    }
  
    if (field === 'start') {
      setOraInizio(formattedTime);
    } else {
      setOraFine(formattedTime);
    }
  };  
  
  const handleCancel = () => {
    if (originalSettings) {
      const formatMinutesToTime = (minutes) => {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
      };
  
      setOraInizio(formatMinutesToTime(originalSettings.night_shift_start));
      setOraFine(formatMinutesToTime(originalSettings.night_shift_end));
    }
    setIsEditing(false);
  };
  
  const handleInputChange = (setter) => (e) => {
    const value = e.target.value;
    setter(value === '' || isNaN(value) ? 0 : parseInt(value, 10));
  };

  const handleSave = async () => {
    if (!oraInizio || !oraFine) {
      Swal.fire({
        icon: "warning",
        title: "Attenzione",
        text: "Inserisci un orario di inizio e fine valido.",
        confirmButtonText: "OK",
      });
      return;
    }

    if (oraInizio === oraFine) {
      Swal.fire({
        icon: "error",
        title: "Errore",
        text: "L'orario di inizio e l'orario di fine non possono essere uguali.",
        confirmButtonText: "OK",
      });
      return;
    }
  
    setIsSaving(true);
  
    try {
      Swal.fire({
        title: 'Salvataggio...',
        allowOutsideClick: false,
        didOpen: () => {
            Swal.showLoading();
        }
      });

      await updateCompanySettings(token, {
        night_shift_active: isNightShiftActive,
        night_shift_start: oraInizio,
        night_shift_end: oraFine,
      });
  
      setOriginalSettings((prev) => ({
        ...prev,
        night_shift_start: oraInizio,
        night_shift_end: oraFine,
      }));
  
      setIsEditing(false); 

      Swal.close();
      Swal.fire({
        icon: "success",
        title: "Salvato!",
        text: "Le impostazioni del lavoro notturno sono state aggiornate.",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      console.error("Errore durante il salvataggio:", error);
      
      let errorMessage = "Non è stato possibile aggiornare l'impostazione. Per favore riprova.";
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        const errorMessages = [];
  
        Object.keys(errorData).forEach((key) => {
          const messageArray = errorData[key];
          if (Array.isArray(messageArray)) {
            errorMessages.push(...messageArray);
          } else if (typeof messageArray === "string") {
            errorMessages.push(messageArray);
          }
        });
  
        if (errorMessages.length > 0) {
          errorMessage = errorMessages.join(" ");
        }
      }

      Swal.close();
      Swal.fire({
        icon: "error",
        title: "Errore",
        text: errorMessage,
        confirmButtonText: "OK",
      });
    } finally {
      setIsSaving(false);
    }
  };


  return (
    <div className="max-w-full px-4 mx-auto">
      <div className="flex justify-between items-center py-2">
      <div className="text-sm font-semibold text-gray-900">
          Abilita l'assegnazione automatica del giustificativo "Lavoro Notturno"
        </div>
  
        {/* {isLoadingSetting ? (
          'Caricamento...'
        ) : (
          <div className="ml-3 flex h-6 items-center">
            <button
              type="button"
              onClick={handleCheckboxChange}
              className={`relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none ${
                isNightShiftActive ? 'bg-indigo-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${
                  isNightShiftActive ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            
            <span className="ml-2 font-semibold text-gray-800">
              Stato: {isNightShiftActive ? 'Abilitato' : 'Disabilitato'}
            </span>
          </div>
        )} */}

        <p className="text-indigo-700 font-semibold">La funzionalità sarà presto disponibile</p>
      </div>
      <div>
      <div className="text-justify pt-2">
          Abilitando questa opzione, il giustificativo "Lavoro Notturno" verrà applicato automaticamente alle fasce orarie interessate.
        </div>
        <div>
          Se necessario, puoi personalizzare il giustificativo su un determinato giorno creando manualmente una richiesta di "Lavoro Notturno": quella creata manualmente avrà la priorità e sovrascriverà quella automatica.
        </div>
      </div>

      {isNightShiftActive && !isLoadingSetting && (
        <div>
          <h2 className="text-sm font-semibold text-gray-900 mb-6 mt-4">
            Impostare l'orario di inizio e l'orario di fine del lavoro notturno.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Ora Inizio */}
            <div className="mb-3 flex items-center">
              <label htmlFor="oraInizio" className="sm:w-40 text-sm font-medium text-gray-700 flex items-center space-x-1 me-4 sm:me-0">
                Ora Inizio
              </label>
              
              <div style={{ position: 'relative', display: 'inline-block', width: 'fit-content' }}>
                {isEditing ? (
                  <input 
                    type="text"
                    placeholder="hh:mm" 
                    value={oraInizio} 
                    onChange={handleOraChange('start')}
                    onBlur={() => handleWorkTimeBlur('start')}
                    className="mt-1 p-1 pl-4 border-gray-200 rounded-md block w-40 shadow-md sm:text-sm" 
                    style={{ paddingRight: '2.5rem' }}
                  />
                ) : (
                  <span className="text-md font-normal text-gray-900 leading-6">
                    {oraInizio || "--:--"}
                  </span>
                )}

                {isEditing && (
                  <span style={{ position: 'absolute', top: '50%', right: '8px', transform: 'translateY(-50%)' }}>
                    <ClockIcon className='w-4 h-4' />
                  </span>
                )}
              </div>
            </div>

            {/* Ora Fine */}
            <div className="mb-3 flex items-center">
              <label htmlFor="oraFine" className="sm:w-40 text-sm font-medium text-gray-700 flex items-center space-x-1 me-4 sm:me-0">
                Ora Fine
              </label>
              
              <div style={{ position: 'relative', display: 'inline-block', width: 'fit-content' }}>
                {isEditing ? (
                  <input 
                    type="text"
                    placeholder="hh:mm" 
                    value={oraFine} 
                    onChange={handleOraChange('end')}
                    onBlur={() => handleWorkTimeBlur('end')}
                    className="mt-1 p-1 pl-4 border-gray-200 rounded-md block w-40 shadow-md sm:text-sm" 
                    style={{ paddingRight: '2.5rem' }}
                  />
                ) : (
                  <span className="text-md font-normal text-gray-900 leading-6">
                    {oraFine || "--:--"}
                  </span>
                )}

                {isEditing && (
                  <span style={{ position: 'absolute', top: '50%', right: '8px', transform: 'translateY(-50%)' }}>
                    <ClockIcon className='w-4 h-4' />
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Pulsanti */}
          <div className="grid md:grid-cols-2 sm:grid-cols-1 gap-7 mt-4">
            <div className="flex justify-center md:justify-end">
              {isEditing ? (
                <button
                  type="button"
                  className="border border-indigo-400 flex items-center rounded-md px-3 py-1 text-center text-sm font-semibold text-white shadow-sm w-24 justify-center mr-3 bg-indigo-600 hover:bg-indigo-400"
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? "Salvataggio..." : "Salva"}
                </button>
              ) : (
                <button
                  type="button"
                  className="border border-indigo-400 flex items-center rounded-md px-3 py-1 text-center text-sm font-semibold text-white shadow-sm w-24 justify-center mr-3 bg-indigo-600 hover:bg-indigo-400"
                  onClick={() => setIsEditing(true)}
                >
                  Modifica
                </button>
              )}
            </div>
              
            {isEditing && (
              <div className="flex justify-center md:justify-start">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="border border-gray-400 block rounded-md px-3 py-1 mr-2 text-center text-sm font-semibold text-gray-700 shadow-sm hover:bg-gray-400 hover:text-white w-24 justify-center"
                >
                  Annulla
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrarioLavoroSetting;
